import React, { useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useLanguage } from '../context/LanguageContext';
import { choiceOptions, choiceHighlights } from '../data/choiceData';
import ScrollToTop from './common/ScrollToTop';

const Choice = () => {
  const { t } = useLanguage();

  // Memoize data for performance
  const memoizedOptions = useMemo(() => choiceOptions, []);
  const memoizedHighlights = useMemo(() => choiceHighlights, []);

  // Scroll progress indicator effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollProgress = document.getElementById('choice-scroll-progress');
      if (scrollProgress) {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight - windowHeight;
        const scrolled = window.scrollY;
        const progress = (scrolled / documentHeight) * 100;
        scrollProgress.style.width = `${Math.min(progress, 100)}%`;
        scrollProgress.setAttribute('aria-valuenow', Math.min(progress, 100).toFixed(0));
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50" role="progressbar" aria-label="Page scroll progress">
        <div className="h-full bg-gradient-to-r from-purple-500 to-red-500 transition-all duration-300 ease-out" style={{width: '0%'}} id="choice-scroll-progress" aria-valuenow={0} aria-valuemin={0} aria-valuemax={100}></div>
      </div>
      <Helmet>
        <title>{t('choice.title', 'Choose Your Path')} - Ravana Migration</title>
        <meta name="description" content={t('choice.subtitle', 'Select how you want to proceed with your study abroad journey')} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-100">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              {t('choice.title', 'Choose Your Path')}
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              {t('choice.subtitle', 'How would you like to proceed with your study abroad journey?')}
            </p>
          </div>

          {/* Choice Highlights */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16" role="list" aria-label="Choice highlights">
            {memoizedHighlights.map((highlight, index) => (
              <div key={index} className="card text-center p-6 hover-lift" role="listitem">
                <div className="text-4xl mb-2" aria-hidden="true">{highlight.icon}</div>
                <div className="text-2xl md:text-3xl font-bold text-blue-600 mb-1">
                  {highlight.value}
                </div>
                <div className="text-sm font-semibold text-gray-900 mb-1">{highlight.title}</div>
                <div className="text-xs text-gray-600">{highlight.description}</div>
              </div>
            ))}
          </div>

          {/* Options Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-12" role="list" aria-label="Choice options">
            {memoizedOptions.map((option) => (
              <div
                key={option.id}
                className={`card card-hover relative ${option.recommended ? 'border-2 border-blue-500' : ''}`}
                role="listitem"
              >
                {option.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                      ⭐ {t('choice.option1.badge', 'Recommended')}
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className="text-6xl mb-4" aria-hidden="true">{option.icon}</div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">
                    {option.title}
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    {option.description}
                  </p>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">What's Included:</h4>
                  <ul className="space-y-2">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-green-600 mt-1">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link to={option.link} className={`btn w-full text-center ${option.recommended ? 'btn-accent' : ''}`}>
                  {option.cta}
                </Link>
              </div>
            ))}
          </div>

          {/* Info Section */}
          <div className="card bg-white bg-opacity-70 backdrop-blur-sm p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Not Sure Which Option to Choose?</h3>
            <p className="text-gray-700 mb-6">
              Our experts are here to help. Schedule a free 15-minute consultation to discuss your goals and get personalized recommendations.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link to="/book-consultation" className="btn btn-accent">
                Book Free Consultation →
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Talk to an Expert →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Mobile CTA - Hidden on choice page as it has its own CTAs */}
      {/* <div className="fixed bottom-4 left-4 right-4 z-50 md:hidden">
        <Link
          to="/wizard"
          className="btn btn-accent w-full text-center text-lg py-4 shadow-2xl animate-pulse-slow"
        >
          Start Assessment →
        </Link>
      </div> */}

      {/* Scroll to Top */}
      <ScrollToTop />
    </>
  );
};

export default Choice;
